import type { CSSProperties, ReactNode } from 'react';

/**
 * TabVault icon library — inline SVG stroke icons.
 *
 * Every glyph lives on a 16×16 grid, drawn with `fill:none`, `stroke:currentColor`,
 * `stroke-width:1.7`, round caps/joins — the design DNA from the handoff screens.
 * Paths are copied verbatim from the `.dc.html` UI-kit specimens where they exist;
 * the few view-affordance glyphs the kits never drew (filter / grid / list) are
 * built from the same primitive geometry so the family stays consistent.
 *
 * Extend by adding a `name` to {@link IconName} and a fragment to `GLYPHS`.
 */
export type IconName =
  | 'search'
  | 'gear'
  | 'chevron-down'
  | 'chevron-left'
  | 'chevron-right'
  | 'check'
  | 'plus'
  | 'minus'
  | 'x'
  | 'trash'
  | 'external'
  | 'restore'
  | 'lock'
  | 'layers'
  | 'clock'
  | 'download'
  | 'sweep'
  | 'sparkle'
  | 'filter'
  | 'grid'
  | 'list'
  | 'folder'
  | 'share'
  | 'link'
  | 'mail'
  | 'moon'
  | 'sun'
  | 'pause'
  | 'monitor';

// Each entry is the inner geometry of the <svg>. A full Record keyed by the
// literal union means indexing with an IconName never yields `undefined`.
const GLYPHS: Record<IconName, ReactNode> = {
  search: (
    <>
      <circle cx="7" cy="7" r="4.4" />
      <path d="M10.4 10.4L14 14" />
    </>
  ),
  gear: (
    <>
      <path d="M8 1.6v2.1M8 12.3v2.1M1.6 8h2.1M12.3 8h2.1M3.5 3.5L5 5M11 11l1.5 1.5M12.5 3.5L11 5M5 11l-1.5 1.5" />
      <circle cx="8" cy="8" r="2.5" />
    </>
  ),
  'chevron-down': <path d="M3.5 6l4.5 4.5L12.5 6" />,
  'chevron-left': <path d="M10 3.5L5.5 8 10 12.5" />,
  'chevron-right': <path d="M6 3.5L10.5 8 6 12.5" />,
  check: <path d="M2.5 8.5L6.5 12.5 13.5 4" />,
  plus: <path d="M8 3v10M3 8h10" />,
  minus: <path d="M3 8h10" />,
  x: <path d="M3.5 3.5l9 9M12.5 3.5l-9 9" />,
  trash: (
    <path d="M2.5 4h11M6.5 4V2.8a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1V4M4 4l.8 9a1.2 1.2 0 0 0 1.2 1.1h4a1.2 1.2 0 0 0 1.2-1.1L12 4" />
  ),
  // arrow leaving a box — used for both "open externally" and "restore" in the kits
  external: <path d="M4.5 11.5l7-7M6.5 4.5h5v5" />,
  restore: <path d="M4.5 11.5l7-7M6.5 4.5h5v5" />,
  lock: (
    <>
      <rect x="3.5" y="7" width="9" height="6.5" rx="1.6" />
      <path d="M5.5 7V5.4a2.5 2.5 0 0 1 5 0V7" />
    </>
  ),
  // two offset rounded squares — the "duplicates across browsers" mark
  layers: (
    <>
      <rect x="1.8" y="5.4" width="8.8" height="8.8" rx="2.2" />
      <rect x="5.4" y="1.8" width="8.8" height="8.8" rx="2.2" />
    </>
  ),
  clock: (
    <>
      <circle cx="8" cy="8" r="6" />
      <path d="M8 4.5V8l2.5 1.5" />
    </>
  ),
  // arrow dropping into a tray — import
  download: (
    <>
      <path d="M8 2v7.4M5 6.4l3 3 3-3" />
      <path d="M2 10.6v2a1.6 1.6 0 0 0 1.6 1.6h8.8A1.6 1.6 0 0 0 14 12.6v-2" />
    </>
  ),
  // the sweep affordance — same tray, slightly taller stem
  sweep: (
    <>
      <path d="M8 1.8v7.4M5 6.4l3 3 3-3" />
      <path d="M2 10.6v2a1.6 1.6 0 0 0 1.6 1.6h8.8A1.6 1.6 0 0 0 14 12.6v-2" />
    </>
  ),
  sparkle: <path d="M8 1.5L9.6 6.4 14.5 8 9.6 9.6 8 14.5 6.4 9.6 1.5 8 6.4 6.4z" />,
  folder: (
    <path d="M2 5.2A1.7 1.7 0 0 1 3.7 3.5H6l1.6 1.9h4.7A1.7 1.7 0 0 1 14 7.1v3.7a1.7 1.7 0 0 1-1.7 1.7H3.7A1.7 1.7 0 0 1 2 10.8z" />
  ),
  share: (
    <>
      <circle cx="4" cy="8" r="1.6" />
      <circle cx="12" cy="4" r="1.6" />
      <circle cx="12" cy="12" r="1.6" />
      <path d="M5.4 7.2l5.2-2.6M5.4 8.8l5.2 2.6" />
    </>
  ),
  link: (
    <>
      <path d="M6.5 9.5a2.4 2.4 0 0 0 3.6.3l2-2a2.4 2.4 0 0 0-3.4-3.4l-1.1 1.1" />
      <path d="M9.5 6.5a2.4 2.4 0 0 0-3.6-.3l-2 2a2.4 2.4 0 0 0 3.4 3.4l1.1-1.1" />
    </>
  ),
  mail: (
    <>
      <rect x="1.8" y="3.5" width="12.4" height="9" rx="1.8" />
      <path d="M2.5 4.5L8 8.8l5.5-4.3" />
    </>
  ),
  moon: <path d="M13.5 9.5A5.5 5.5 0 0 1 6.5 2.5a5.5 5.5 0 1 0 7 7z" />,
  sun: (
    <>
      <circle cx="8" cy="8" r="3" />
      <path d="M8 1.4v1.8M8 12.8v1.8M1.4 8h1.8M12.8 8h1.8M3.4 3.4l1.3 1.3M11.3 11.3l1.3 1.3M12.6 3.4l-1.3 1.3M4.7 11.3l-1.3 1.3" />
    </>
  ),
  pause: <path d="M5 3.5v9M11 3.5v9" />,
  monitor: (
    <>
      <rect x="2" y="3" width="12" height="8.5" rx="1.6" />
      <path d="M5.5 14h5" />
    </>
  ),
  // ---- built from primitives to complete the set (no specimen in the kits) ----
  filter: <path d="M2 3.5h12l-4.6 5.4v3.6l-2.8 1.4V8.9z" />,
  grid: (
    <>
      <rect x="2" y="2" width="5" height="5" rx="1.3" />
      <rect x="9" y="2" width="5" height="5" rx="1.3" />
      <rect x="2" y="9" width="5" height="5" rx="1.3" />
      <rect x="9" y="9" width="5" height="5" rx="1.3" />
    </>
  ),
  list: <path d="M2 4h2M6 4h8M2 8h2M6 8h8M2 12h2M6 12h8" />,
};

export interface IconProps {
  name: IconName;
  /** pixel box, applied to width & height (viewBox stays 16×16) */
  size?: number;
  strokeWidth?: number;
  /** stroke color; defaults to currentColor so icons inherit text color */
  color?: string;
  className?: string;
  style?: CSSProperties;
  /** when set, the icon is announced to assistive tech; otherwise it's decorative */
  title?: string;
}

export function Icon({
  name,
  size = 16,
  strokeWidth = 1.7,
  color = 'currentColor',
  className,
  style,
  title,
}: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={style}
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
    >
      {title ? <title>{title}</title> : null}
      {GLYPHS[name]}
    </svg>
  );
}
